(() => {
  if (customElements.get('aftersoil-gallery-rail')) return;

  class AftersoilGalleryRail extends HTMLElement {
    connectedCallback() {
      this.carousel = document.getElementById(this.getAttribute('aria-controls'));
      this.track = this.querySelector('.as-mobile-gallery-rail__track');
      this.currentOutput = this.querySelector('.as-mobile-gallery-rail__current');
      this.totalOutput = this.querySelector('.as-mobile-gallery-rail__total');

      if (!this.carousel || !this.track || !this.currentOutput || !this.totalOutput) return;

      this.activePointer = null;
      this.currentIndex = 0;
      this.frame = null;
      this.abortController = new AbortController();
      const signal = this.abortController.signal;

      this.carousel.addEventListener('scroll', () => this.scheduleSync(), { passive: true, signal });
      this.carousel.addEventListener('scrollend', () => this.syncFromCarousel(), { signal });
      this.track.addEventListener('pointerdown', event => this.onPointerDown(event), { signal });
      this.track.addEventListener('pointermove', event => this.onPointerMove(event), { signal });
      this.track.addEventListener('pointerup', event => this.onPointerUp(event), { signal });
      this.track.addEventListener('pointercancel', event => this.onPointerUp(event), { signal });
      this.track.addEventListener('keydown', event => this.onKeyDown(event), { signal });

      this.observer = new MutationObserver(() => this.scheduleSync());
      this.observer.observe(this.carousel, {
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'hidden']
      });

      this.syncFromCarousel();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.observer?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
    }

    get slides() {
      return Array.from(this.carousel.children).filter(slide => !slide.hidden);
    }

    scheduleSync() {
      if (this.frame || this.activePointer !== null) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.syncFromCarousel();
      });
    }

    syncFromCarousel() {
      const slides = this.slides;
      if (slides.length < 2) {
        this.hidden = true;
        return;
      }

      this.hidden = false;
      const selected = slides.findIndex(slide => slide.classList.contains('is-selected'));
      const viewportCenter = this.carousel.scrollLeft + this.carousel.clientWidth / 2;
      const nearest = slides.reduce((best, slide, index) => {
        const center = slide.offsetLeft + slide.offsetWidth / 2;
        const distance = Math.abs(center - viewportCenter);
        return distance < best.distance ? { index, distance } : best;
      }, { index: 0, distance: Infinity }).index;

      this.update(selected >= 0 ? selected : nearest, slides.length);
    }

    update(index, total = this.slides.length) {
      const safeIndex = Math.max(0, Math.min(total - 1, index));
      const progress = total > 1 ? safeIndex / (total - 1) * 100 : 0;
      this.currentIndex = safeIndex;
      this.style.setProperty('--as-gallery-progress', `${progress}%`);
      this.currentOutput.value = this.pad(safeIndex + 1);
      this.currentOutput.textContent = this.pad(safeIndex + 1);
      this.totalOutput.textContent = this.pad(total);
      this.track.setAttribute('aria-valuemax', String(total));
      this.track.setAttribute('aria-valuenow', String(safeIndex + 1));
      this.track.setAttribute('aria-valuetext', `Image ${safeIndex + 1} of ${total}`);
    }

    select(index, behavior = 'auto') {
      const slides = this.slides;
      if (!slides.length) return;
      const safeIndex = Math.max(0, Math.min(slides.length - 1, index));
      const target = slides[safeIndex];
      this.update(safeIndex, slides.length);
      this.carousel.scrollTo({ left: target.offsetLeft, behavior });
    }

    indexFromPointer(event) {
      const bounds = this.track.getBoundingClientRect();
      const ratio = Math.max(0, Math.min(1, (event.clientX - bounds.left) / bounds.width));
      return Math.round(ratio * (this.slides.length - 1));
    }

    onPointerDown(event) {
      if (event.button !== undefined && event.button !== 0) return;
      event.preventDefault();
      event.stopPropagation();
      this.activePointer = event.pointerId;
      this.classList.add('is-dragging');
      this.track.setPointerCapture(event.pointerId);
      this.select(this.indexFromPointer(event));
    }

    onPointerMove(event) {
      if (event.pointerId !== this.activePointer) return;
      event.preventDefault();
      event.stopPropagation();
      this.select(this.indexFromPointer(event));
    }

    onPointerUp(event) {
      if (event.pointerId !== this.activePointer) return;
      event.preventDefault();
      event.stopPropagation();
      if (this.track.hasPointerCapture(event.pointerId)) this.track.releasePointerCapture(event.pointerId);
      this.activePointer = null;
      this.classList.remove('is-dragging');
      this.select(this.currentIndex, 'smooth');
    }

    onKeyDown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.select(this.currentIndex - 1, 'smooth');
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.select(this.currentIndex + 1, 'smooth');
      } else if (event.key === 'Home') {
        event.preventDefault();
        this.select(0, 'smooth');
      } else if (event.key === 'End') {
        event.preventDefault();
        this.select(this.slides.length - 1, 'smooth');
      }
    }

    pad(value) {
      return String(value).padStart(2, '0');
    }
  }

  customElements.define('aftersoil-gallery-rail', AftersoilGalleryRail);
})();
